import { useEffect, useState } from "react";
import { AlarmClock, AlarmClockOff } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { avisarContadores } from "../../services/contadoresDaLista";
import { estadoDaPausa } from "../../helpers/tempoDeEspera";
import { lerPrefTickets } from "../../services/prefsTickets";
import { cn } from "../../lib/utils";

// Faixa da conversa em pausa, logo acima do campo de escrever.
//
// Existe por causa da resposta: quem volta a falar com o cliente antes da hora
// precisa dizer o que fazer com o relógio — manter o compromisso ou desligar.
// Antes essa escolha não existia: a mensagem do cliente apagava a pausa
// sozinha, e a hora combinada se perdia sem ninguém decidir nada.
const AvisoDePausa = ({ ticket }) => {
  const [salvando, setSalvando] = useState(false);
  // "Manter" some com a faixa até a conversa mudar de estado — quem já
  // decidiu não precisa da pergunta em cima do teclado o dia inteiro. Guarda
  // QUAL estado foi dispensado, não um sim/não: assim o vencimento traz a
  // faixa de volta sozinho, sem um efeito só para desfazer a dispensa.
  const [dispensadoEm, setDispensadoEm] = useState(null);
  const piscar = lerPrefTickets("avisoPausaPiscar", true);

  const pausa = estadoDaPausa(ticket);
  // O vencimento é estado novo: manter escondido o que a pessoa dispensou
  // enquanto o prazo corria esconderia justamente o pedido de retorno.
  const chaveDoEstado = `${ticket?.id}|${ticket?.snoozedUntil}|${pausa?.vencido}`;

  // O prazo vence pela passagem do tempo, e nada re-renderiza a conversa por
  // isso. Sem este despertador a faixa continuaria dizendo "em pausa até 14h"
  // depois das 14h, com a pessoa olhando para ela.
  const [, redesenhar] = useState(0);
  const prazoEm = ticket?.snoozedUntil
    ? new Date(ticket.snoozedUntil).getTime()
    : null;

  useEffect(() => {
    if (!prazoEm) return undefined;

    const falta = prazoEm - Date.now();

    if (falta <= 0) return undefined;

    // Teto do setTimeout do navegador: acima disso ele dispara na hora.
    const id = setTimeout(
      () => redesenhar((n) => n + 1),
      Math.min(falta + 1000, 2147483000),
    );

    return () => clearTimeout(id);
  }, [prazoEm]);

  if (!pausa || dispensadoEm === chaveDoEstado) return null;

  const desligar = async () => {
    setSalvando(true);

    try {
      await api.put(`/tickets/${ticket.id}/snooze`, { minutos: 0 });
      avisarContadores("pausa");
      toast.success("Relógio desligado. O atendimento voltou para a lista.");
    } catch (err) {
      toastError(err);
    }

    setSalvando(false);
  };

  return (
    <div
      role="status"
      className={cn(
        "flex flex-none flex-wrap items-center gap-2 border-t px-3 py-2 text-sm",
        pausa.vencido
          ? "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300"
          : pausa.comMensagem
            ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
            : "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
      )}
    >
      <AlarmClock
        className={cn(
          "h-4 w-4 shrink-0",
          pausa.vencido && piscar && "animate-pulse",
        )}
      />
      <p className="min-w-0 flex-1">
        {pausa.vencido
          ? `Você marcou para voltar em ${pausa.rotulo} — o prazo venceu.`
          : pausa.comMensagem
            ? `Em pausa até ${pausa.rotulo}, e o cliente escreveu.`
            : `Em pausa até ${pausa.rotulo}.`}
      </p>

      <button
        type="button"
        onClick={() => setDispensadoEm(chaveDoEstado)}
        className="rounded-md px-2 py-1 text-xs font-medium underline-offset-2 hover:underline"
      >
        Manter o relógio
      </button>
      <button
        type="button"
        disabled={salvando}
        onClick={desligar}
        className="flex items-center gap-1.5 rounded-md border border-current px-2 py-1 text-xs font-medium disabled:opacity-60"
      >
        <AlarmClockOff className="h-3.5 w-3.5" />
        Desligar relógio
      </button>
    </div>
  );
};

export default AvisoDePausa;
